import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { PreferredPartnerService } from '../../preferred-partner.service';
import { BonusType, LoadBonus, LoadBonusesResponse, SummaryRegion } from '../../types';
import { formatLocalDate, mondayDateFilter, parseISODateLocal } from '../../utils/date';

interface BonusFilters {
  weekStart: Date | null;
  bonusType: BonusType | 'all';
  regionId: number | null;
}

const PAGE_SIZE = 50;

/**
 * /preferred-partner/bonuses
 *
 * Paginated table of load bonuses for the carrier with totals (server-side,
 * NOT a client-side sum of the current page).
 */
@Component({
  selector: 'lohi-ppp-load-bonuses',
  templateUrl: './ppp-load-bonuses.component.html',
  styleUrls: ['./ppp-load-bonuses.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PppLoadBonusesComponent implements OnInit, OnDestroy {
  public readonly form: FormGroup;
  public readonly response$: Observable<LoadBonusesResponse | null>;
  public readonly regions$: Observable<SummaryRegion[]>;
  public readonly loading$$ = new BehaviorSubject<boolean>(false);
  public readonly error$$ = new BehaviorSubject<string | null>(null);
  public readonly pageIndex$$ = new BehaviorSubject<number>(0);
  public readonly pageSize = PAGE_SIZE;
  public readonly mondayDateFilter = mondayDateFilter;

  private destroy$$ = new Subject<void>();

  constructor(private fb: FormBuilder, private pppService: PreferredPartnerService) {
    this.form = this.fb.group({
      weekStart: [null as Date | null],
      bonusType: ['all' as BonusType | 'all'],
      regionId: [null as number | null],
    });

    this.regions$ = this.pppService.summary$.pipe(map((s) => s?.byRegion || []));

    // Read form.value lazily on each emission (instead of capturing it via
    // startWith at constructor time): when the user lands here from another
    // page that already filled summary$ / currentWeek$, ngOnInit's
    // combineLatest emits synchronously and patches the form BEFORE the
    // async pipe subscribes. Using `map(() => this.form.value)` ensures the
    // first emission picks up the patched values instead of the stale
    // constructor snapshot.
    const filters$ = this.form.valueChanges.pipe(
      startWith(null),
      map(() => this.form.value as BonusFilters),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      tap(() => this.pageIndex$$.next(0)),
    );

    this.response$ = combineLatest([filters$, this.pageIndex$$.pipe(distinctUntilChanged())]).pipe(
      filter(([f]: [BonusFilters, number]) => !!f.weekStart),
      tap(() => {
        this.loading$$.next(true);
        this.error$$.next(null);
      }),
      switchMap(([f, pageIndex]: [BonusFilters, number]) =>
        this.pppService
          .listLoadBonuses$({
            weekStart: formatLocalDate(f.weekStart) || '',
            bonusType: f.bonusType === 'all' ? undefined : f.bonusType,
            regionId: f.regionId || undefined,
            limit: PAGE_SIZE,
            offset: pageIndex * PAGE_SIZE,
          })
          .pipe(
            tap(() => this.loading$$.next(false)),
            catchError((err) => {
              this.loading$$.next(false);
              this.error$$.next(err?.message || 'Failed to load bonuses');
              return of<LoadBonusesResponse | null>(null);
            }),
          ),
      ),
    );
  }

  public ngOnInit(): void {
    // Seed weekStart from /calendar/current_week and regionId from the
    // first region in the summary in a single patch, so the user lands on
    // a populated view (and we avoid a double fetch from two patches).
    combineLatest([
      this.pppService.currentWeek$.pipe(filter((cw) => !!cw)),
      this.pppService.summary$.pipe(filter((s) => !!s)),
    ])
      .pipe(take(1), takeUntil(this.destroy$$))
      .subscribe(([cw, summary]) => {
        const regions = summary?.byRegion || [];
        const updates: Partial<BonusFilters> = {};
        if (cw && !this.form.value.weekStart) {
          updates.weekStart = parseISODateLocal(cw.weekStart);
        }
        if (regions.length > 0 && this.form.value.regionId == null) {
          updates.regionId = regions[0].regionId;
        }
        if (Object.keys(updates).length > 0) {
          this.form.patchValue(updates, { emitEvent: true });
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  public onPageChange(event: PageEvent): void {
    this.pageIndex$$.next(event.pageIndex);
  }

  public trackByBonus(index: number, item: LoadBonus): number {
    return item.id;
  }
}
